import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { ArticleCard } from '../components/ArticleCard';
import { Search, Filter, Loader2, X, SlidersHorizontal } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const articleTypes = [
  { value: 'research', label: 'Recherche empirique' },
  { value: 'case_study', label: 'Étude de cas' },
  { value: 'review', label: 'Revue de littérature' },
  { value: 'practice_note', label: 'Note de pratique' },
  { value: 'protocol', label: 'Protocole' }
];

export default function ArticlesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [selectedTypes, setSelectedTypes] = useState(
    searchParams.get('type') ? [searchParams.get('type')] : []
  );
  const [page, setPage] = useState(0);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const limit = 10;

  useEffect(() => {
    fetchArticles();
  }, [page, selectedTypes, searchParams]);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        status: 'published',
        skip: page * limit,
        limit: limit.toString()
      });
      
      const searchQuery = searchParams.get('search');
      if (searchQuery) params.append('search', searchQuery);
      if (selectedTypes.length === 1) params.append('article_type', selectedTypes[0]);

      const res = await fetch(`${API_URL}/api/articles?${params}`);
      if (res.ok) {
        const data = await res.json();
        setArticles(data.articles || []);
        setTotal(data.total || 0);
      }
    } catch (e) {
      console.error('Failed to fetch articles:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    if (search) {
      searchParams.set('search', search);
    } else {
      searchParams.delete('search');
    }
    setSearchParams(searchParams);
  };

  const handleTypeToggle = (type) => {
    setPage(0);
    if (selectedTypes.includes(type)) {
      setSelectedTypes(selectedTypes.filter(t => t !== type));
      searchParams.delete('type');
    } else {
      setSelectedTypes([type]);
      searchParams.set('type', type);
    }
    setSearchParams(searchParams);
  };

  const clearFilters = () => {
    setSelectedTypes([]);
    setSearch('');
    setSearchParams({});
    setPage(0);
  };

  const totalPages = Math.ceil(total / limit);
  const hasFilters = selectedTypes.length > 0 || searchParams.get('search');

  const FilterSidebar = () => (
    <div className="space-y-6">
      {/* Article Type Filter */}
      <div>
        <h3 className="font-medium text-sm mb-3">Type d'article</h3>
        <div className="space-y-2">
          {articleTypes.map((type) => (
            <div key={type.value} className="flex items-center">
              <Checkbox
                id={type.value}
                checked={selectedTypes.includes(type.value)}
                onCheckedChange={() => handleTypeToggle(type.value)}
              />
              <Label
                htmlFor={type.value}
                className="ml-2 text-sm font-normal cursor-pointer"
              >
                {type.label}
              </Label>
            </div>
          ))}
        </div>
      </div>

      {/* Clear Filters */}
      {hasFilters && (
        <Button variant="outline" size="sm" onClick={clearFilters} className="w-full gap-2">
          <X className="w-4 h-4" />
          Effacer les filtres
        </Button>
      )}
    </div>
  );

  return (
    <div data-testid="articles-page" className="min-h-screen bg-background">
      {/* Page Header */}
      <div className="bg-muted/30 border-b">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
          <h1 className="font-serif text-3xl font-bold mb-2">Articles</h1>
          <p className="text-muted-foreground">
            Parcourez nos publications en psychothérapie
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Search Bar */}
        <form onSubmit={handleSearch} className="mb-6">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Rechercher par titre, mots-clés, auteur..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Button type="submit" data-testid="search-btn">
              Rechercher
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              className="lg:hidden gap-2"
              onClick={() => setShowMobileFilters(!showMobileFilters)}
            >
              <SlidersHorizontal className="w-4 h-4" />
              Filtres
            </Button>
          </div>
        </form>

        {/* Mobile Filters */}
        {showMobileFilters && (
          <Card className="mb-6 lg:hidden">
            <CardContent className="pt-6">
              <FilterSidebar />
            </CardContent>
          </Card>
        )}

        <div className="flex gap-8">
          {/* Desktop Sidebar */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Filter className="w-4 h-4" />
                  Filtres
                </CardTitle>
              </CardHeader>
              <CardContent>
                <FilterSidebar />
              </CardContent>
            </Card>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {/* Results info */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-muted-foreground">
                {total} article{total !== 1 ? 's' : ''} trouvé{total !== 1 ? 's' : ''}
                {hasFilters && ' (filtré)'}
              </p>
              {hasFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs">
                  Effacer les filtres
                </Button>
              )}
            </div>

            {/* Articles List */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : articles.length > 0 ? (
              <>
                <div className="space-y-4">
                  {articles.map((article) => (
                    <ArticleCard key={article.article_id} article={article} />
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-8 pt-8 border-t">
                    <Button
                      variant="outline"
                      onClick={() => setPage(p => Math.max(0, p - 1))}
                      disabled={page === 0}
                      data-testid="prev-page-btn"
                    >
                      Précédent
                    </Button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i;
                        } else if (page < 3) {
                          pageNum = i;
                        } else if (page > totalPages - 4) {
                          pageNum = totalPages - 5 + i;
                        } else {
                          pageNum = page - 2 + i;
                        }
                        return (
                          <Button
                            key={pageNum}
                            variant={page === pageNum ? 'default' : 'ghost'}
                            size="sm"
                            onClick={() => setPage(pageNum)}
                            className="w-10"
                          >
                            {pageNum + 1}
                          </Button>
                        );
                      })}
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                      disabled={page >= totalPages - 1}
                      data-testid="next-page-btn"
                    >
                      Suivant
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Search className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-4">Aucun article trouvé</p>
                  {hasFilters && (
                    <Button variant="outline" onClick={clearFilters}>
                      Effacer les filtres
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
